import { Injectable } from '@angular/core';
import { Observable, Subject, pipe, combineLatest, merge, forkJoin, of } from 'rxjs';
import { map, tap, take, switchMap, skip, filter, share, mergeMap, shareReplay } from 'rxjs/operators';
import { ScreenService, PagesService, TaxonomyStoreService } from '@app/data';
import { DefaultIndexDoc, TaxonomyGQL, SortInput, TaxonomyTermPages, SortOrder, QueryOperator } from '@app/graphql';
import { Chip, Route, ParentRoute, SubPage, ParentSubPage, Facet, School, Partner, Question, Person, Taxonomies, CONSTANTS } from '@app/constants';
import { emitOnce } from '@ngneat/elf';

@Injectable({
  providedIn: 'root'
})
export class TaxonomyService {

	public taxonomy$ = this.taxonomyStore.taxonomy$;
	public taxonomyDone$;
	public facets$ = this.taxonomyStore.facets$;
	
	constructor (
		private fetchTaxonomy: TaxonomyGQL,
		private taxonomyStore: TaxonomyStoreService
	) {
		  const taxonomy$: Observable<any> = this.fetchTaxonomy.fetch({
		    filter: {
		      conditions: [
		      	{field: "status", operator: QueryOperator.NotEqual, value: ["false"]},
			      {
			        field: "vid",
			        value: ["tags", "pages", "schools", "partners", "questions", "about", "subpages"]
			      }
		      ]
		    },
		    limit: 300,
		    sort: [{field: "weight", direction: SortOrder.Asc}]
		  }).pipe(
	      map((res:any) => res.data.taxonomyTermQuery.entities),
	      map((items: any[]) => items.map(item => {

	      	//console.log('**** TaxonomyService getTaxonomy item', item);

	      	const field_image_url = item.fieldPoster ? item.fieldPoster.url : undefined;
	      	const field_logo_1 = item.fieldLogo ? item.fieldLogo.url : undefined;
	      	const newItem = {
	      		...item,
	      		url: item.entityUrl.path,
	      		field_image_url,
	      		field_logo_1,
	      		field_logo_margin_1: item.fieldLogoMargin,
	      		body: item.description?.value,
	      		field_video_id: item.fieldVideoId,
	      		field_video_platform: item.fieldVideoPlatform ? item.fieldVideoPlatform.targetId.toString() : null
	      	}

	      	return newItem;
	      })),
	      tap((entities: any[]) => this.taxonomyStore.setTaxonomyStore(entities)),
	      take(1),
	      shareReplay({ bufferSize: 1, refCount: true })
	    );

		  const pages$: Observable<Taxonomies[]> = taxonomy$.pipe(
		    map(pages => {
		      return pages.filter((taxonomy: any) => taxonomy.entityBundle === 'pages');
		    }),
		    tap((pages: Taxonomies[]) => {
		      this.taxonomyStore.setStore('sekce', pages);
		    })
		  );

		  const routes$: Observable<Taxonomies[]> = pages$.pipe(
		    mergeMap(pages => 
		      forkJoin([
		        of(pages.filter((page: Taxonomies) => page.queryParent.entities.length !== 0)),
		        of(pages.filter((page: Taxonomies) => page.queryParent.entities.length === 0))
		      ])
		     ),
		    map(([routes, mainRoutes]) => {
		    	emitOnce(() => {
		    		this.taxonomyStore.setParentStore('routes', mainRoutes);
		    		routes.map((route:Taxonomies) => {
		    		  this.taxonomyStore.addChild('routes', route.queryParent.entities[0].entityId ,route);
		    		});
		    	});
		    	return mainRoutes;			      
		    })
		  );

		  const tags$: Observable<Taxonomies[]> = taxonomy$.pipe(
		    map(taxonomies => {
		      return taxonomies.filter((taxonomy: Taxonomies) => taxonomy.entityBundle === 'tags');
		    }),
		    tap((tags: Taxonomies[]) => {
		      this.taxonomyStore.setStore('stitky', tags);
		    })
		  );

		  const schools$: Observable<Taxonomies[]> = taxonomy$.pipe(
		    map(taxonomies => {
		      return taxonomies.filter((taxonomy: Taxonomies) => taxonomy.entityBundle === 'schools');
		    }),
		    tap((schools: Taxonomies[]) => {
		      this.taxonomyStore.setStore('skoly', schools);
		    })
		  );

		  const partners$: Observable<Taxonomies[]> = taxonomy$.pipe(
		    map(taxonomies => {
		      return taxonomies.filter((taxonomy: Taxonomies) => taxonomy.entityBundle === 'partners');
		    }),
		    tap((partners: Taxonomies[]) => {
		      this.taxonomyStore.setStore('partneri', partners);
		    })
		  );

		  const questions$: Observable<Taxonomies[]> = taxonomy$.pipe(
		    map(taxonomies => {
		      return taxonomies.filter((taxonomy: Taxonomies) => taxonomy.entityBundle === 'questions');
		    }),
		    tap((questions: Taxonomies[]) => {
		      this.taxonomyStore.setStore('questions', questions);
		    })
		  );

		  const about$: Observable<Taxonomies[]> = taxonomy$.pipe(
		    map(taxonomies => {
		      return taxonomies.filter((taxonomy: Taxonomies) => taxonomy.entityBundle === 'about');
		    }),
		    tap((about: Taxonomies[]) => {
		      this.taxonomyStore.setStore('about', about);
		    })
		  );

		  const subPages$: Observable<Taxonomies[]> = taxonomy$.pipe(
		    map((pages: any[]) => {
		      return pages.filter((taxonomy: Taxonomies) => taxonomy.entityBundle === 'subpages');
		    }),
		    mergeMap(pages => 
		      forkJoin([
		        of(pages.filter((page: Taxonomies) => page.queryParent.entities.length !== 0)),
		        of(pages.filter((page: Taxonomies) => page.queryParent.entities.length === 0))
		      ])
		     ),
		    map(([routes, mainRoutes]) => {
		    	emitOnce(() => {
		    		this.taxonomyStore.setParentStore('subpages', mainRoutes);
		    		routes.map((route: Taxonomies) => {
		    			let newRoute = {
		    				subRoute: true,
		    				queryParams: {
		    					podstranka: route.entityUrl.path,
		    				},
		    				path: './',
		    				... route
		    			}; 
		    		  this.taxonomyStore.addChild('subpages', route.queryParent.entities[0].entityId ,newRoute);
		    		});
		    	});
		    	return mainRoutes;
		    })
		   );

		  this.taxonomyDone$ = combineLatest([
		    routes$,
		    tags$,
		    schools$,
		    partners$,
		    questions$,
		    //people$,
		    about$,
		    subPages$
		    ]).pipe(
		    	take(1),
		    	tap(_ => console.log('**** TaxonomyService getTaxonomy taxonomyDone$ _ ', _)),
		    	shareReplay({ bufferSize: 1, refCount: true })
		    );

		    /*this.taxonomyDone$.subscribe((data: any) => {
		    	console.log('**** TaxonomyService getTaxonomy data', data);
		    });*/
	}

	public getChipsFromArticle = (article: any): any[] => {
	  //console.log('FetchService setTags article ', article);

	  let tags: Chip[] = [];

	  /* articles */

	  if (article && article.stitky && article.stitky.length > 0) {
	    const chips = this.taxonomyStore.getEntities('stitky', article.stitky);
	    tags = [...chips, ...tags];
	  }

	  if (article && article.skoly && article.skoly.length > 0) {
	    const chips = this.taxonomyStore.getEntities('skoly', article.skoly);
	    tags = [...chips, ...tags];
	  }

	  if (article && article.partneri && article.partneri.length > 0) {
	    const chips = this.taxonomyStore.getEntities('partneri', article.partneri);
	    tags = [...chips, ...tags];
	  }

	  /* taxonomy */

	  if (article && article.fieldTags && article.fieldTags.length > 0) {

	  	let taxonomy: number[] = [];

	  	article.fieldTags.map((chip: any) => {
	  		taxonomy.push(chip.targetId);
	  	});

	    const chips = this.taxonomyStore.getEntities('stitky', taxonomy);
	    tags = [...chips];
	  }

	  return tags;
	}

}